<template>
    <pt-dialog
        center
        ref="experienceDialogRef"
        :model-value="true"
        :show-close="fullscreen"
        :fullscreen="fullscreen"
        :custom-class="[
            $style.dialog,
            fullscreen ? $style.fullscreen : '',
            $style[props.component],
            props.headIcon ? $style.headIcon : ''
        ]"
        :top="fullscreen ? '0px' : '20px'"
        :width="fullscreen ? '100%' : 'calc(100% - 92px)'"
        @opened="handleOpened"
        @close="handleClose"
    >
        <template #title>
            <div class="pt-dialog__back" v-if="props.visibleBack" @click="handleBack">
                <pt-icon
                    icon="pt-icon--back"
                    :style="{
                        width: '18px',
                        height: '18px',
                        fill: '#344563'
                    }"
                ></pt-icon>
                <pt-link type="text" color="black" dom-type="a" size="large">
                    <span>{{ t('header.btn_back') }}</span>
                </pt-link>
            </div>
            <pt-icon
                v-if="!fullscreen"
                icon="pt-icon-solid-close"
                class="pt-dialog__outside-close"
                @click="handleClose"
            ></pt-icon>
        </template>

        <header>
            <div :class="$style.icon" v-if="headIcon">
                <pt-icon
                    :icon="headIcon"
                    :style="{
                        width: '54px',
                        height: '54px'
                    }"
                ></pt-icon>
            </div>
            <div :class="$style.title" v-if="props.title">
                <span>{{ props.title }}</span>
            </div>
        </header>
        <slot></slot>
    </pt-dialog>
</template>

<script lang="ts">
export default {
    name: 'ExperienceDialog'
};
</script>

<script lang="ts" setup>
import useEventBus from '@assets/plugins/eventBus';
import { ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

// interface
interface Props {
    title: string;
    component: string;
    headIcon?: string;
    visibleBack: boolean;
    fullscreen: boolean;
}

// props
const props = withDefaults(defineProps<Props>(), {
    visibleBack: true,
    fullscreen: false
});

// emits
const emit = defineEmits(['close', 'back']);

onUnmounted(() => {
    experienceDialogRef.value?.dialogRef?.removeEventListener('scroll', handleScroll, false);
});

// state
const { t } = useI18n();
const { eventBus } = useEventBus();
const experienceDialogRef = ref();

function handleClose() {
    emit('close');
    experienceDialogRef.value?.dialogRef?.removeEventListener('scroll', handleScroll, false);
}

function handleBack() {
    emit('back');
}

function handleOpened() {
    experienceDialogRef.value.dialogRef.addEventListener('scroll', handleScroll, false);
}

function handleScroll() {
    eventBus.$emit('experienceDialogScroll');
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.dialog {
    width: calc(100% - 90px);
    height: calc(100vh - 40px);
    margin-bottom: 0 !important;

    &.ExperienceABTestTypeSelect {
        :global {
            .pt-dialog__body {
                max-width: 90%;
                margin: 0 auto;
            }
        }
    }

    &.ExperienceNonABTestList {
        height: auto;
        :global {
            .pt-dialog__body {
                padding-bottom: 56px !important;
            }
        }
    }

    &.ExperienceTemplateBlank {
        height: auto;

        :global {
            .pt-dialog__footer {
                display: none;
            }
            .pt-dialog__header,
            .pt-dialog__body {
                padding: 0 !important;
            }
        }

        header {
            display: none;
        }
    }

    &.ExperienceForm {
        :global {
            .pt-dialog__body {
                header {
                    padding: 16% 0 56px;
                }
            }
        }

        &.headIcon {
            :global {
                .pt-dialog__body {
                    header {
                        padding: 10% 0 56px;
                    }
                }
            }
        }
    }

    :global {
        .pt-dialog {
            border-radius: 8px;
        }
        .pt-dialog__header {
            padding: 0 48px !important;

            .pt-dialog__back {
                height: 56px;
                padding: 18px 0;
                position: absolute;
                top: 0;
            }

            .pt-dialog__outside-close {
                position: absolute;
                width: 28px;
                height: 28px;
                fill: #b3bac5;
                right: -36px;
                top: 0;
                cursor: pointer;
                transition: 0.15s linear;
                &:hover {
                    fill: #dfe1e6;
                }
            }
        }
        .pt-dialog__body {
            padding: 0 12% !important;
            min-height: 340px;
        }
        .pt-dialog__footer {
            padding: 0 0 64px !important;
        }
    }

    header {
        padding: 56px 0;

        .title {
            font-size: 32px;
            font-weight: normal;
            text-align: center;
        }

        .icon {
            text-align: center;
            margin-bottom: 36px;
        }
    }
}

.fullscreen {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;

    :global {
        .pt-dialog {
            border-radius: 0px;
        }
        .pt-dialog__body {
            padding: 0 !important;
        }
        .pt-dialog__footer {
            display: none;
        }
    }
}
</style>
